import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  Add,
  Delete,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  PlayArrow,
  Done,
  Refresh,
  FactCheck,
  OpenInNew,
  Schedule,
  HourglassBottom,
  AssignmentTurnedIn,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Download,
  PictureAsPdf,
} from '@mui/icons-material';
import axios from 'axios';
import { WarehouseAPI } from '../services/api';
import { usePermission } from '../hooks/usePermission';
import PageHeader from '../components/common/PageHeader';
import StatPill from '../components/common/StatPill';
import { useToast } from '../components/common/useToast';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import { eyebrow } from '../components/actionables/brand';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const CYCLIC_COUNT_API_BASE = `${WarehouseAPI.LOGGING_BASE}/api/cyclic-counts`;

// ─── Types ───────────────────────────────────────────────────────────────────

interface BaselineItem {
  shelfCode: string;
  skuCode: string;
  baselineQty: number;
  inventoryType: string;
}

interface CountedItem {
  shelfCode: string;
  skuCode: string;
  countedQty: number;
}

interface Adjustment {
  _id: string;
  skuCode: string;
  shelfCode: string;
  baselineQty: number;
  countedQty: number;
  variance: number;
  operation: 'ADD' | 'SUBTRACT' | 'NONE';
  adjusted: boolean;
  adjustedAt?: string;
}

interface CyclicCount {
  _id: string;
  refNumber: string;
  userId: string;
  status: 'setup' | 'counting' | 'variance_review' | 'completed';
  shelves: string[];
  baselineInventory: BaselineItem[];
  countedInventory: CountedItem[];
  adjustments: Adjustment[];
  createdAt: string;
  updatedAt: string;
}

interface SkuAdjustmentGroup {
  skuCode: string;
  netVariance: number;
  lastAdjustedDate: string;
  logs: any[];
}

interface ShelfInfo {
  shelfCode: string;
  quantity: number;
}

interface ShelfStatus {
  counted: ShelfInfo[];
  pending: ShelfInfo[];
  totalCounted: number;
  totalPending: number;
  completeness: number;
  metrics: {
    inventoryCountedToday: number;
    dailyInventoryRate: number;
    daysToComplete: number;
    totalDaysActive: number;
    totalWarehouseQty: number;
    totalCountedQty: number;
    totalPendingQty: number;
    totalNetVariance: number;
  };
}

type PageView = 'list' | 'wizard';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = ['Setup', 'Add Shelves', 'Physical Count', 'Variance & Adjust', 'Complete'];

function statusToStep(status: CyclicCount['status']): number {
  switch (status) {
    case 'setup': return 1;
    case 'counting': return 2;
    case 'variance_review': return 3;
    case 'completed': return 4;
    default: return 1;
  }
}

// Client-side sort for the Variance Report tables (Step 3 interactive + Step 4 read-only).
// "expected" maps to baselineQty, "counted" to countedQty. Sort runs over the FULL adjustments
// list before the table slices it for pagination, so the page order stays consistent.
type VarianceSortKey = 'shelf' | 'sku' | 'expected' | 'counted' | 'variance';
type SortDir = 'asc' | 'desc';

function sortAdjustments(list: Adjustment[], key: VarianceSortKey, dir: SortDir): Adjustment[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'shelf':
        return factor * String(a.shelfCode || '').localeCompare(String(b.shelfCode || ''), undefined, { numeric: true, sensitivity: 'base' });
      case 'sku':
        return factor * String(a.skuCode || '').localeCompare(String(b.skuCode || ''), undefined, { numeric: true, sensitivity: 'base' });
      case 'expected':
        return factor * (a.baselineQty - b.baselineQty);
      case 'counted':
        return factor * (a.countedQty - b.countedQty);
      case 'variance':
        return factor * (a.variance - b.variance);
      default:
        return 0;
    }
  });
}

function statusLabel(status: CyclicCount['status']): { label: string; color: 'default' | 'primary' | 'warning' | 'error' | 'success' | 'info'; icon: React.ReactElement } {
  switch (status) {
    case 'setup': return { label: 'Setup', color: 'default', icon: <Schedule fontSize="small" /> };
    case 'counting': return { label: 'In Progress', color: 'warning', icon: <HourglassBottom fontSize="small" /> };
    case 'variance_review': return { label: 'Pending Adjustment', color: 'info', icon: <FactCheck fontSize="small" /> };
    case 'completed': return { label: 'Completed', color: 'success', icon: <AssignmentTurnedIn fontSize="small" /> };
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function localInputsKey(id: string) { return `cyclic-count-inputs-${id}`; }
function saveInputsLocally(id: string, inputs: Record<string, number>) {
  try { localStorage.setItem(localInputsKey(id), JSON.stringify(inputs)); } catch (_) {}
}
function loadInputsLocally(id: string): Record<string, number> | null {
  try { const raw = localStorage.getItem(localInputsKey(id)); return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
}
function clearInputsLocally(id: string) {
  try { localStorage.removeItem(localInputsKey(id)); } catch (_) {}
}

function extractAdjustError(err: any): string {
  return (
    err?.response?.data?.inventoryAdjustmentResponses?.[0]?.errors?.[0]?.description ||
    err?.response?.data?.inventoryAdjustmentResponses?.[0]?.errors?.[0]?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    'Unicommerce adjustment failed'
  );
}

// Compact, unambiguous timestamp, e.g. "23 Oct 2026, 11:55 PM" — short month name
// (avoids DD/MM vs MM/DD confusion) and no seconds. Guards against an invalid/empty date.
// Matches the shared Inventory date format. Display-only — do NOT use for CSV/PDF export content.
function formatDate(dateString: string): string {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const VarianceBadge: React.FC<{ variance: number }> = ({ variance }) => {
  if (variance === 0) return <Chip label="No variance" size="small" color="success" variant="outlined" />;
  if (variance > 0)
    return (
      <Chip
        icon={<TrendingUp fontSize="small" />}
        label={`+${variance}`}
        size="small"
        color="warning"
        sx={{ fontWeight: 700 }}
      />
    );
  return (
    <Chip
      icon={<TrendingDown fontSize="small" />}
      label={`${variance}`}
      size="small"
      color="error"
      sx={{ fontWeight: 700 }}
    />
  );
};

// Compact sort control for the mobile card view of the Variance Report tables. The desktop
// tables sort via TableSortLabel column headers, which don't exist in card view — so on mobile
// we surface the SAME shared sort state (varianceSortKey/dir) through a small select + direction
// toggle. It calls the EXISTING handleVarianceSort/setVarianceSortDir; no new sort logic.
const VARIANCE_SORT_OPTIONS: { value: VarianceSortKey; label: string }[] = [
  { value: 'variance', label: 'Variance' },
  { value: 'sku', label: 'SKU Code' },
  { value: 'shelf', label: 'Shelf' },
  { value: 'expected', label: 'Baseline' },
  { value: 'counted', label: 'Counted' },
];

const VarianceSortControl: React.FC<{
  sortKey: VarianceSortKey;
  sortDir: SortDir;
  onSortKeyChange: (key: VarianceSortKey) => void;
  onToggleDir: () => void;
}> = ({ sortKey, sortDir, onSortKeyChange, onToggleDir }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Sort by</Typography>
    <TextField
      select
      size="small"
      value={sortKey}
      onChange={(e) => onSortKeyChange(e.target.value as VarianceSortKey)}
      sx={{ minWidth: 130 }}
    >
      {VARIANCE_SORT_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
    <Tooltip title={sortDir === 'asc' ? 'Ascending' : 'Descending'}>
      <IconButton size="small" onClick={onToggleDir} aria-label="Toggle sort direction">
        {sortDir === 'asc' ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
      </IconButton>
    </Tooltip>
  </Box>
);

const MetricsSummary: React.FC<{ status: ShelfStatus | null }> = ({ status }) => {
  if (!status) return null;
  const { metrics, completeness } = status;
  const netVariance = metrics.totalNetVariance;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <StatPill
          variant="summary"
          label="Inventory Completeness"
          value={`${Math.round(completeness)}%`}
          accent="#DC5829"
          icon={<FactCheck fontSize="small" />}
        />
        <StatPill
          variant="summary"
          label="Counted Today"
          value={metrics.inventoryCountedToday.toLocaleString()}
          accent="#16A34A"
          icon={<CheckCircle fontSize="small" />}
        />
        <StatPill
          variant="summary"
          label="Daily Rate"
          value={metrics.dailyInventoryRate.toLocaleString()}
          accent="#2563EB"
          icon={<TrendingUp fontSize="small" />}
        />
        <StatPill
          variant="summary"
          label="Est. Remaining (days)"
          value={metrics.daysToComplete.toLocaleString()}
          accent="#D97706"
          icon={<Schedule fontSize="small" />}
        />
        <StatPill
          variant="summary"
          label="Net Variance"
          value={`${netVariance > 0 ? '+' : ''}${netVariance.toLocaleString()}`}
          accent={netVariance >= 0 ? '#16A34A' : '#DC2626'}
          icon={netVariance >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
        />
      </Box>
      {/* Completeness progress affordance — informative, kept beneath the pill row */}
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem', mb: 0.5 }}>
          {metrics.totalCountedQty.toLocaleString()} / {metrics.totalWarehouseQty.toLocaleString()} units counted
        </Typography>
        <LinearProgress
          variant="determinate"
          value={completeness}
          sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(220, 88, 41, 0.12)', '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundImage: 'linear-gradient(90deg, #DC5829 0%, #F26A2E 100%)' } }}
        />
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CyclicCountPage: React.FC = () => {
  // RBAC: creating/submitting counts and pushing adjustments are cyclic-count mutations.
  // View-only callers see these controls hidden/disabled; the backend independently rejects
  // the writes with 403 (defense in depth, SI-5).
  const canEdit = usePermission('cyclic-count').can('edit');

  // Mobile/responsive fallback. Mirrors BundleToolTables (<md → card/scroll fallbacks).
  // Desktop (≥md) renders every table exactly as before — isMobile is false there.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Page mode. pageView stays in local state (NOT URL-backed): the wizard view depends on the
  // `cyclicCount` object, which is fetched on resume and never persisted, so forcing 'wizard' on
  // refresh would land on an empty wizard. Only the list-view tab + its sort/pagination are
  // URL-backed (prefix "cc").
  const setUrlParams = useSetUrlParams();
  const [pageView, setPageView] = useState<PageView>('list');
  const [tabParam, setTabParam] = useUrlParam('cctab', '0');
  const activeTab = parseInt(tabParam, 10) || 0;
  const setActiveTab = (v: number) => setTabParam(v === 0 ? '0' : String(v));
  const [counts, setCounts] = useState<CyclicCount[]>([]);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [listError, setListError] = useState('');

  // All Adjustments state
  const [allAdjustments, setAllAdjustments] = useState<any[]>([]);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  const [expandedSku, setExpandedSku] = useState<string | null>(null);

  // Shelf Status state
  const [shelfStatus, setShelfStatus] = useState<ShelfStatus | null>(null);
  const [loadingShelfStatus, setLoadingShelfStatus] = useState(false);

  // Added export menu UI state
  const [csvAnchorEl, setCsvAnchorEl] = useState<null | HTMLElement>(null);
  const [pdfAnchorEl, setPdfAnchorEl] = useState<null | HTMLElement>(null);

  // Group adjustments by SKU
  const groupedAdjustments = React.useMemo(() => {
    const groups: Record<string, SkuAdjustmentGroup> = {};
    
    allAdjustments.forEach(adj => {
      if (!groups[adj.skuCode]) {
        groups[adj.skuCode] = {
          skuCode: adj.skuCode,
          netVariance: 0,
          lastAdjustedDate: adj.adjustedAt,
          logs: []
        };
      }
      
      const group = groups[adj.skuCode];
      group.netVariance += adj.variance;
      group.logs.push(adj);
      
      if (new Date(adj.adjustedAt) > new Date(group.lastAdjustedDate)) {
        group.lastAdjustedDate = adj.adjustedAt;
      }
    });
    
    // Convert to array and sort by latest adjusted date
    return Object.values(groups).sort((a, b) => 
      new Date(b.lastAdjustedDate).getTime() - new Date(a.lastAdjustedDate).getTime()
    );
  }, [allAdjustments]);

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [cyclicCount, setCyclicCount] = useState<CyclicCount | null>(null);

  // Step 0 – Setup
  const [refNumber, setRefNumber] = useState('');
  const [creatingCount, setCreatingCount] = useState(false);
  const [setupError, setSetupError] = useState('');

  // Step 1 – Shelves
  const [shelfInput, setShelfInput] = useState('');
  const [addingShelf, setAddingShelf] = useState(false);
  const [proceedingToCount, setProceedingToCount] = useState(false);
  const [shelfError, setShelfError] = useState('');
  const [shelfWarning, setShelfWarning] = useState('');
  const [shelfSuccess, setShelfSuccess] = useState('');

  // Step 2 – Counting
  // countInputs: { [shelfCode_skuCode]: countedQty }
  const [countInputs, setCountInputs] = useState<Record<string, number>>({});
  const [newSkuInputs, setNewSkuInputs] = useState<Record<string, { sku: string; qty: number }>>({});
  const [submittingCount, setSubmittingCount] = useState(false);
  const [countError, setCountError] = useState('');

  // Step 3 – Variance & Adjust
  const [adjusting, setAdjusting] = useState<Record<string, boolean>>({});
  const [adjustError, setAdjustError] = useState('');
  const [adjustAllLoading, setAdjustAllLoading] = useState(false);
  const [confirmAdjustAll, setConfirmAdjustAll] = useState(false);

  // Variance Report sort + pagination (shared across Step 3 interactive and Step 4 read-only
  // tables — they are never visible at once). Default to variance desc so the largest-magnitude
  // variances surface first.
  const [varianceSortKeyParam] = useUrlParam('ccvsort', 'variance');
  const [varianceSortDirParam] = useUrlParam('ccvdir', 'desc');
  const varianceSortKey = varianceSortKeyParam as VarianceSortKey;
  const varianceSortDir = varianceSortDirParam as SortDir;
  const [variancePageParam, setVariancePageParam] = useUrlParam('ccvpage', '0');
  const variancePage = parseInt(variancePageParam, 10) || 0;
  const setVariancePage = (v: number) => setVariancePageParam(v === 0 ? '0' : String(v));

  // Shelf Status list pagination (list view tab 2).
  const [pendingShelfPageParam, setPendingShelfPageParam] = useUrlParam('ccppage', '0');
  const pendingShelfPage = parseInt(pendingShelfPageParam, 10) || 0;
  const setPendingShelfPage = (v: number) => setPendingShelfPageParam(v === 0 ? '0' : String(v));
  const [countedShelfPageParam, setCountedShelfPageParam] = useUrlParam('cccpage', '0');
  const countedShelfPage = parseInt(countedShelfPageParam, 10) || 0;
  const setCountedShelfPage = (v: number) => setCountedShelfPageParam(v === 0 ? '0' : String(v));

  const handleVarianceSort = (key: VarianceSortKey) => {
    let nextKey = varianceSortKey;
    let nextDir: SortDir;
    if (key === varianceSortKey) {
      nextDir = varianceSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      nextKey = key;
      // Numeric columns default to desc (largest first); text columns default to asc.
      nextDir = key === 'shelf' || key === 'sku' ? 'asc' : 'desc';
    }
    // Key + dir + the variance-table page reset go through one URL update.
    setUrlParams({
      ccvsort: nextKey === 'variance' ? null : nextKey,
      ccvdir: nextDir === 'desc' ? null : nextDir,
      ccvpage: null,
    });
  };

  // Mobile card-view direction toggle — flips the current direction and resets the page in one
  // URL update (keeps the sort key unchanged).
  const toggleVarianceDir = () => {
    const nextDir: SortDir = varianceSortDir === 'asc' ? 'desc' : 'asc';
    setUrlParams({ ccvdir: nextDir === 'desc' ? null : nextDir, ccvpage: null });
  };

  // SKU Search
  const [skuOptions, setSkuOptions] = useState<any[]>([]);
  const [searchingSku, setSearchingSku] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (pageView === 'list') {
      if (activeTab === 0) fetchCounts();
      else if (activeTab === 1) fetchAllAdjustments();
      
      // Always fetch metrics summary when in list view
      fetchShelfStatus();
    }
  }, [pageView, activeTab]);

  // Reset the variance table page when the active count changes, so the user always lands on page
  // 1 of the newly-loaded list. (Sort changes reset the page inside handleVarianceSort, in the
  // same URL update.)
  useEffect(() => {
    setVariancePage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cyclicCount?._id]);

  // Reset shelf-status list pages whenever the shelf status data reloads.
  useEffect(() => {
    setPendingShelfPage(0);
    setCountedShelfPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelfStatus]);

  const fetchCounts = async () => {
    setLoadingCounts(true);
    setListError('');
    try {
      const res = await axios.get(CYCLIC_COUNT_API_BASE, { headers: getAuthHeaders() });
      setCounts(res.data.data || []);
    } catch (err: any) {
      setListError(err.response?.data?.message || err.message || 'Failed to load cyclic counts');
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchAllAdjustments = async () => {
    setLoadingAdjustments(true);
    setListError('');
    try {
      const res = await axios.get(`${CYCLIC_COUNT_API_BASE}/adjustments`, { headers: getAuthHeaders() });
      setAllAdjustments(res.data.data || []);
    } catch (err: any) {
      setListError(err.response?.data?.message || err.message || 'Failed to load adjustments');
    } finally {
      setLoadingAdjustments(false);
    }
  };

  const fetchShelfStatus = async () => {
    setLoadingShelfStatus(true);
    setListError('');
    try {
      const res = await axios.get(`${CYCLIC_COUNT_API_BASE}/shelf-status`, { headers: getAuthHeaders() });
      setShelfStatus(res.data.data || null);
    } catch (err: any) {
      setListError(err.response?.data?.message || err.message || 'Failed to load shelf status');
    } finally {
      setLoadingShelfStatus(false);
    }
  };

  // Re-run the list-view fetches for the current tab (mirrors the list-view effect above). Wired to
  // the Retry affordance on the shared listError alert. NOTE: listError is shared across the three
  // fetches, so a stale message from one failed fetch can briefly remain after an unrelated fetch
  // succeeds; Retry re-triggers the fetches that back the current view and clears it on success.
  const retryListFetches = () => {
    if (activeTab === 0) fetchCounts();
    else if (activeTab === 1) fetchAllAdjustments();
    fetchShelfStatus();
  };

  const resetWizardState = () => {
    setActiveStep(0);
    setCyclicCount(null);
    setRefNumber('');
    setCreatingCount(false);
    setSetupError('');
    setShelfInput('');
    setAddingShelf(false);
    setShelfError('');
    setShelfSuccess('');
    setCountInputs({});
    setNewSkuInputs({});
    setSubmittingCount(false);
    setCountError('');
    setAdjusting({});
    setAdjustError('');
    setAdjustAllLoading(false);
    setConfirmAdjustAll(false);
  };

  const buildInitialCountInputs = (count: CyclicCount) => {
    const initial: Record<string, number> = {};
    for (const item of count.baselineInventory) {
      initial[`${item.shelfCode}::${item.skuCode}`] = item.baselineQty;
    }
    if (count.countedInventory?.length) {
      for (const item of count.countedInventory) {
        initial[`${item.shelfCode}::${item.skuCode}`] = item.countedQty;
      }
    }
    return initial;
  };

  const handleOpenNew = () => {
    resetWizardState();
    setPageView('wizard');
  };

  const handleBackToList = () => {
    setPageView('list');
  };

  const handleResume = async (count: CyclicCount) => {
    try {
      const res = await axios.get(`${CYCLIC_COUNT_API_BASE}/${count._id}`, { headers: getAuthHeaders() });
      const fullCount: CyclicCount = res.data.data;
      setCyclicCount(fullCount);
      const step = statusToStep(fullCount.status);
      setActiveStep(step);

      if (step >= 2) {
        const saved = loadInputsLocally(fullCount._id);
        if (saved) {
          setCountInputs(saved);
        } else {
          setCountInputs(buildInitialCountInputs(fullCount));
        }
      }

      setPageView('wizard');
    } catch (err: any) {
      setListError(err.response?.data?.message || err.message || 'Failed to load cyclic count details');
    }
  };

  // ── Step 0: Create count ──────────────────────────────────────────────────

  const handleCreateCount = async () => {
    if (!refNumber.trim()) { setSetupError('Please enter a reference number'); return; }
    setCreatingCount(true);
    setSetupError('');
    try {
      const res = await axios.post(
        CYCLIC_COUNT_API_BASE,
        { refNumber: refNumber.trim() },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      setCyclicCount(res.data.data);
      setActiveStep(1);
      setPageView('wizard');
    } catch (err: any) {
      setSetupError(err.response?.data?.message || err.message || 'Failed to create cyclic count');
    } finally {
      setCreatingCount(false);
    }
  };

  // ── Step 1: Add shelves ───────────────────────────────────────────────────

  const handleAddShelf = async () => {
    if (!shelfInput.trim()) { setShelfError('Please enter a shelf code'); return; }
    if (!cyclicCount) return;
    setAddingShelf(true);
    setShelfError('');
    setShelfSuccess('');
    try {
      const res = await axios.post(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}/shelves`,
        { shelfCode: shelfInput.trim() },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      const { shelfCode, snapshot } = res.data.data;
      setCyclicCount(res.data.data.count);
      setShelfInput('');
      setShelfSuccess(`Shelf ${shelfCode} added — ${snapshot.length} SKUs snapshotted`);
    } catch (err: any) {
      setShelfError(err.response?.data?.message || err.message || 'Failed to add shelf');
    } finally {
      setAddingShelf(false);
    }
  };

  const handleRemoveShelf = async (shelfCode: string) => {
    if (!cyclicCount) return;
    try {
      const res = await axios.delete(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}/shelves/${shelfCode}`,
        { headers: getAuthHeaders() }
      );
      setCyclicCount(res.data.data);
      setShelfSuccess('');
    } catch (err: any) {
      setShelfError(err.response?.data?.message || err.message || 'Failed to remove shelf');
    }
  };

  const handleProceedToCount = async () => {
    if (!cyclicCount || cyclicCount.shelves.length === 0) {
      setShelfError('Please add at least one shelf before proceeding');
      return;
    }

    setProceedingToCount(true);
    setShelfError('');
    setShelfWarning('');
    setShelfSuccess('');

    let latestCount = cyclicCount;

    try {
      await WarehouseAPI.refreshStockFromUnicommerce();
      setShelfSuccess('Stock sync triggered. Capturing latest available inventory...');
    } catch (err: any) {
      setShelfWarning(`Stock sync trigger failed (${err?.message || 'network issue'}). Proceeding with latest available local snapshot.`);
    }

    try {
      const res = await axios.post(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}/refresh-baseline`,
        {},
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      latestCount = res.data.data?.count || cyclicCount;
      setCyclicCount(latestCount);
      const totalSkus = (res.data.data?.shelfSummary || []).reduce((sum: number, item: any) => sum + (item.skuCount || 0), 0);
      setShelfSuccess(`Inventory refreshed — latest baseline captured (${totalSkus} SKUs)`);
    } catch (err: any) {
      setShelfError(err.response?.data?.message || err.message || 'Failed to refresh inventory before count');
      setProceedingToCount(false);
      return;
    }

    const saved = loadInputsLocally(cyclicCount._id);
    if (saved) {
      setCountInputs(saved);
    } else {
      setCountInputs(buildInitialCountInputs(latestCount));
    }

    setActiveStep(2);
    setProceedingToCount(false);
  };

  // ── Step 2: Count items ───────────────────────────────────────────────────

  const handleCountChange = useCallback((shelfCode: string, skuCode: string, value: string) => {
    const qty = Math.max(0, parseInt(value || '0', 10) || 0);
    setCountInputs((prev) => {
      const next = { ...prev, [`${shelfCode}::${skuCode}`]: qty };
      if (cyclicCount) saveInputsLocally(cyclicCount._id, next);
      return next;
    });
  }, [cyclicCount]);

  const handleSkuSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setSkuOptions([]);
      return;
    }
    setSearchingSku(true);
    try {
      const results = await WarehouseAPI.searchSkusFromRedshift(query);
      setSkuOptions(results || []);
    } catch (err) {
      console.error('SKU search failed:', err);
      setSkuOptions([]);
    } finally {
      setSearchingSku(false);
    }
  };

  const handleNewSkuChange = (shelfCode: string, field: 'sku' | 'qty', value: string) => {
    setNewSkuInputs((prev) => ({
      ...prev,
      [shelfCode]: {
        sku: field === 'sku' ? value.toUpperCase() : (prev[shelfCode]?.sku || ''),
        qty: field === 'qty' ? Math.max(0, parseInt(value || '0', 10) || 0) : (prev[shelfCode]?.qty || 0),
      },
    }));
  };

  const handleAddNewSku = (shelfCode: string) => {
    const entry = newSkuInputs[shelfCode];
    if (!entry?.sku?.trim()) return;
    const key = `${shelfCode}::${entry.sku.trim()}`;
    setCountInputs((prev) => ({ ...prev, [key]: entry.qty || 0 }));
    setNewSkuInputs((prev) => ({ ...prev, [shelfCode]: { sku: '', qty: 0 } }));
  };

  const handleSubmitCount = async () => {
    if (!cyclicCount) return;
    setSubmittingCount(true);
    setCountError('');
    try {
      const counted: CountedItem[] = [];
      const processed = new Set<string>();

      for (const shelf of cyclicCount.shelves) {
        const baselineItems = cyclicCount.baselineInventory.filter((b) => b.shelfCode === shelf);
        for (const item of baselineItems) {
          const key = `${shelf}::${item.skuCode}`;
          let qty = countInputs[key];
          if (qty === undefined) qty = item.baselineQty;
          counted.push({ shelfCode: shelf, skuCode: item.skuCode, countedQty: qty });
          processed.add(key);
        }
      }

      for (const [key, qty] of Object.entries(countInputs)) {
        if (!processed.has(key)) {
          const [shelfCode, skuCode] = key.split('::');
          counted.push({ shelfCode, skuCode, countedQty: qty });
        }
      }

      const res = await axios.put(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}/counted`,
        { counted },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      setCyclicCount(res.data.data);
      clearInputsLocally(cyclicCount._id);
      setActiveStep(3);
    } catch (err: any) {
      setCountError(err.response?.data?.message || err.message || 'Failed to submit count');
    } finally {
      setSubmittingCount(false);
    }
  };

  // ── Step 3: Adjust variances ──────────────────────────────────────────────

  const handleAdjustOne = async (adj: Adjustment) => {
    if (adj.operation === 'NONE' || adj.adjusted) return;
    setAdjusting((prev) => ({ ...prev, [adj._id]: true }));
    setAdjustError('');
    try {
      const res = await axios.post(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount!._id}/adjustments/${adj._id}/execute`,
        {},
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );

      if (res.data.requiresClientExecution) {
        // Execute N8N webhook directly from user's browser to bypass server-side IP blocks
        // Guarded: a given count adjustment is only ever allowed to reach Unicommerce once.
        const ucResponse = await WarehouseAPI.postInventoryAdjust(
          res.data.n8nPayload,
          `cyclic-adjust:${cyclicCount!._id}:${adj._id}`
        );

        const adjustmentResponse = ucResponse.data?.inventoryAdjustmentResponses?.[0];
        if (adjustmentResponse && adjustmentResponse.successful === false) {
          const innerError = adjustmentResponse.errors?.[0];
          throw new Error(innerError?.description || innerError?.message || 'Unicommerce adjustment failed');
        }

        // Commit execution result back to backend to complete DB transactions
        const finalRes = await axios.post(
          `${CYCLIC_COUNT_API_BASE}/${cyclicCount!._id}/adjustments/${adj._id}/execute`,
          {
            clientExecuted: true,
            unicommerceResponse: ucResponse.data
          },
          { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
        );
        setCyclicCount(finalRes.data.data);
      } else {
        setCyclicCount(res.data.data);
      }

      toast(`Adjusted ${adj.skuCode}: ${adj.operation === 'ADD' ? '+' : ''}${adj.variance}`, { duration: 3000 });
    } catch (err: any) {
      setAdjustError(`Failed to adjust ${adj.skuCode}: ${extractAdjustError(err)}`);
      // Refresh count to get updated failure state
      try {
        const res = await axios.get(
          `${CYCLIC_COUNT_API_BASE}/${cyclicCount!._id}`,
          { headers: getAuthHeaders() }
        );
        setCyclicCount(res.data.data);
      } catch (_) {}
    } finally {
      setAdjusting((prev) => ({ ...prev, [adj._id]: false }));
    }
  };

  const handleAdjustAll = async () => {
    if (!cyclicCount) return;
    setAdjustAllLoading(true);
    setAdjustError('');
    const pending = cyclicCount.adjustments.filter((a) => a.operation !== 'NONE' && !a.adjusted);
    for (const adj of pending) {
      try {
        const res = await axios.post(
          `${CYCLIC_COUNT_API_BASE}/${cyclicCount!._id}/adjustments/${adj._id}/execute`,
          {},
          { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
        );

        if (res.data.requiresClientExecution) {
          // Execute N8N webhook directly from user's browser to bypass server-side IP blocks
          const ucResponse = await WarehouseAPI.postInventoryAdjust(
            res.data.n8nPayload,
            `cyclic-adjust:${cyclicCount!._id}:${adj._id}`
          );

          const adjustmentResponse = ucResponse.data?.inventoryAdjustmentResponses?.[0];
          if (adjustmentResponse && adjustmentResponse.successful === false) {
            const innerError = adjustmentResponse.errors?.[0];
            throw new Error(innerError?.description || innerError?.message || 'Unicommerce adjustment failed');
          }

          // Commit execution result back to backend to complete DB transactions
          await axios.post(
            `${CYCLIC_COUNT_API_BASE}/${cyclicCount!._id}/adjustments/${adj._id}/execute`,
            {
              clientExecuted: true,
              unicommerceResponse: ucResponse.data
            },
            { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
          );
        }
      } catch (err: any) {
        console.error(`Bulk adjustment failed for ${adj.skuCode}:`, err);
      }
    }
    // Refresh count
    try {
      const res = await axios.get(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}`,
        { headers: getAuthHeaders() }
      );
      setCyclicCount(res.data.data);
      if (res.data.data.status === 'completed') setActiveStep(4);
    } catch (_) {}
    setAdjustAllLoading(false);
    setConfirmAdjustAll(false);
  };

  const handleMarkComplete = async () => {
    if (!cyclicCount) return;

    const pending = cyclicCount.adjustments.filter((a) => a.operation !== 'NONE' && !a.adjusted);
    if (pending.length > 0) {
      setAdjustError(`Please complete all pending adjustments before marking complete (${pending.length} remaining).`);
      return;
    }

    try {
      const res = await axios.patch(
        `${CYCLIC_COUNT_API_BASE}/${cyclicCount._id}`,
        { status: 'completed' },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      setCyclicCount(res.data.data);
    } catch (err: any) {
      setAdjustError(err.response?.data?.message || err.message || 'Failed to mark cyclic count complete');
      return;
    }
    setActiveStep(4);
  };

  const handleDownloadCSV = (shelfWise: boolean) => {
    setCsvAnchorEl(null);
    const headers = ['SKU Code', 'Shelf Code', 'Baseline Qty', 'Counted Qty', 'Variance', 'Operation', 'Net Variance', 'Last Adjusted Date'];
    
    // Create an expanded list of rows from groupedAdjustments where each log is a row
    const rows: any[] = [];
    groupedAdjustments.forEach(group => {
      group.logs.forEach(log => {
        rows.push([
          log.skuCode,
          log.shelfCode,
          log.baselineQty,
          log.countedQty,
          log.variance,
          log.operation,
          group.netVariance,
          new Date(log.adjustedAt).toLocaleString()
        ]);
      });
    });

    if (shelfWise) {
      rows.sort((a, b) => String(a[1] || '').localeCompare(String(b[1] || '')));
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map((value: any) => `"${value}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Variance_Report_${shelfWise ? 'Shelf_Wise_' : ''}${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = (shelfWise: boolean) => {
    setPdfAnchorEl(null);
    const doc = new jsPDF();
    doc.text(`Variance Report${shelfWise ? ' (Shelf-Wise)' : ''}`, 14, 15);
    
    const headers = [['SKU Code', 'Shelf Code', 'Baseline', 'Counted', 'Variance', 'Operation']];
    const data: any[] = [];
    
    groupedAdjustments.forEach(group => {
      group.logs.forEach(log => {
        data.push([
          log.skuCode,
          log.shelfCode,
          log.baselineQty,
          log.countedQty,
          log.variance,
          log.operation
        ]);
      });
    });

    if (shelfWise) {
      data.sort((a, b) => String(a[1] || '').localeCompare(String(b[1] || '')));
    }

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 20,
    });

    doc.save(`Variance_Report_${shelfWise ? 'Shelf_Wise_' : ''}${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ── List view ─────────────────────────────────────────────────────────────

  const renderListView = () => (
    <Box>
      <PageHeader
        icon={<FactCheck sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
        title="Cyclic Counts"
        subtitle="Start a new cyclic count or resume any in-progress count."
        actions={canEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenNew}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
          >
            New Cyclic Count
          </Button>
        )}
      />

      {/* Metrics Summary Bar */}
      <MetricsSummary status={shelfStatus} />

      {listError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={retryListFetches}>
              Retry
            </Button>
          }
        >
          {listError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Cyclic Counts" />
          <Tab label="All Adjustments" />
          <Tab label="Shelf Status" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        loadingCounts ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : counts.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="h6" mb={1}>No cyclic counts yet</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create your first cyclic count to begin shelf-wise physical verification.
              </Typography>
              {canEdit && (
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>
                  Create New Cyclic Count
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Horizontal-scroll wrap (CardingTab pattern): on narrow widths the 7-column overview
             scrolls instead of breaking layout. overflowX+minWidth don't change desktop rendering. */
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)', overflow: 'hidden', overflowX: 'auto' }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#faf8f3', '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Shelves</TableCell>
                  <TableCell align="center">SKUs Counted</TableCell>
                  <TableCell align="center">Adjustments</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counts.map((count) => {
                  const label = statusLabel(count.status);
                  const varianceRows = count.adjustments?.filter((a) => a.operation !== 'NONE') || [];
                  const adjustedRows = varianceRows.filter((a) => a.adjusted).length;
                  return (
                    <TableRow key={count._id} hover>
                      <TableCell>
                        <Typography fontWeight={700}>{count.refNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">{count._id.slice(-8)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={label.icon}
                          label={label.label}
                          size="small"
                          color={label.color}
                          variant={count.status === 'completed' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">{count.shelves.length}</TableCell>
                      <TableCell align="center">{count.countedInventory?.length || 0}</TableCell>
                      <TableCell align="center">
                        {varianceRows.length === 0
                          ? <Chip label="0" size="small" variant="outlined" />
                          : <Chip label={`${adjustedRows}/${varianceRows.length}`} size="small" color={adjustedRows === varianceRows.length ? 'success' : 'warning'} variant="outlined" />}
                      </TableCell>
                      <TableCell>{formatDate(count.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNew fontSize="small" />}
                          onClick={() => handleResume(count)}
                          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                        >
                          {count.status === 'completed' ? 'View' : 'Resume'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {activeTab === 1 && (
        loadingAdjustments ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : groupedAdjustments.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="h6" mb={1}>No adjustments found</Typography>
              <Typography variant="body2" color="text.secondary">
                There are no variance adjustments recorded yet.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <StatPill
                  variant="summary"
                  label="Total Adjustments"
                  value={groupedAdjustments.length}
                  accent="#DC5829"
                  icon={<FactCheck fontSize="small" />}
                />
                <StatPill
                  variant="summary"
                  label="Net Warehouse Variance"
                  value={`${groupedAdjustments.reduce((sum, g) => sum + g.netVariance, 0) > 0 ? '+' : ''}${groupedAdjustments.reduce((sum, g) => sum + g.netVariance, 0)}`}
                  accent={groupedAdjustments.reduce((sum, g) => sum + g.netVariance, 0) >= 0 ? '#16A34A' : '#DC2626'}
                  icon={groupedAdjustments.reduce((sum, g) => sum + g.netVariance, 0) >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="outlined" startIcon={<Download />} onClick={(e) => setCsvAnchorEl(e.currentTarget)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}>
                  Download CSV
                </Button>
                <Menu
                  anchorEl={csvAnchorEl}
                  open={Boolean(csvAnchorEl)}
                  onClose={() => setCsvAnchorEl(null)}
                >
                  <MenuItem onClick={() => handleDownloadCSV(false)}>Default (SKU-Wise)</MenuItem>
                  <MenuItem onClick={() => handleDownloadCSV(true)}>Shelf-Wise</MenuItem>
                </Menu>

                <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={(e) => setPdfAnchorEl(e.currentTarget)} color="error" sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}>
                  Download PDF
                </Button>
                <Menu
                  anchorEl={pdfAnchorEl}
                  open={Boolean(pdfAnchorEl)}
                  onClose={() => setPdfAnchorEl(null)}
                >
                  <MenuItem onClick={() => handleDownloadPDF(false)}>Default (SKU-Wise)</MenuItem>
                  <MenuItem onClick={() => handleDownloadPDF(true)}>Shelf-Wise</MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Horizontal-scroll wrap (CardingTab pattern): the collapsible grouped-adjustments
                table (with nested logs sub-table) is too complex for a card view, so on narrow
                widths it scrolls horizontally. Expand/collapse + View Logs are unchanged. */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)', overflow: 'hidden', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#faf8f3', '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                  <TableCell padding="checkbox" />
                  <TableCell>SKU Code</TableCell>
                  <TableCell align="center">Net Variance</TableCell>
                  <TableCell>Last Adjusted Date</TableCell>
                  <TableCell align="right">Logs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedAdjustments.map((group) => (
                  <React.Fragment key={group.skuCode}>
                    <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedSku(expandedSku === group.skuCode ? null : group.skuCode)}
                        >
                          {expandedSku === group.skuCode ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>{group.skuCode}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <VarianceBadge variance={group.netVariance} />
                      </TableCell>
                      <TableCell>{formatDate(group.lastAdjustedDate)}</TableCell>
                      <TableCell align="right">
                        <Button 
                          size="small" 
                          onClick={() => setExpandedSku(expandedSku === group.skuCode ? null : group.skuCode)}
                          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                        >
                          {expandedSku === group.skuCode ? 'Hide Logs' : 'View Logs'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedSku === group.skuCode} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2, bgcolor: '#faf8f3', p: 2, borderRadius: '12px' }}>
                            <Typography variant="subtitle2" gutterBottom component="div" fontWeight={700}>
                              Complete Logs for {group.skuCode}
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                                  <TableCell>Reference</TableCell>
                                  <TableCell>Shelf</TableCell>
                                  <TableCell align="center">Variance</TableCell>
                                  <TableCell>Operation</TableCell>
                                  <TableCell>Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.logs.map((log) => (
                                  <TableRow key={log.adjustmentId}>
                                    <TableCell>{log.refNumber}</TableCell>
                                    <TableCell>{log.shelfCode}</TableCell>
                                    <TableCell align="center">
                                      <VarianceBadge variance={log.variance} />
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={log.operation === 'ADD' ? 'Added' : 'Removed'} 
                                        size="small" 
                                        color={log.operation === 'ADD' ? 'success' : 'error'} 
                                        variant="outlined" 
                                      />
                                    </TableCell>
                                    <TableCell>{formatDate(log.adjustedAt)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )
    )}

      {activeTab === 2 && (
        loadingShelfStatus ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !shelfStatus ? (
          <Card>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="h6" mb={1}>No shelf status data</Typography>
              <Typography variant="body2" color="text.secondary">
                Failed to load shelf status data.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={4}>
            {/* Status Summary */}
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', bgcolor: '#faf8f3', border: '1px solid #ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" color="primary.main">Warehouse Completeness</Typography>
                  <Typography variant="body2" color="primary.dark">
                    {shelfStatus.counted.length} of {shelfStatus.counted.length + shelfStatus.pending.length} shelves counted
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" color="primary.main">
                    {Math.round((shelfStatus.counted.length / (shelfStatus.counted.length + shelfStatus.pending.length || 1)) * 100)}%
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Total Coverage</Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Pending Shelves */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" mr={1.5}>Pending Shelves</Typography>
                  <Chip label={shelfStatus.pending.length} size="small" color="warning" sx={{ fontWeight: 700 }} />
                </Box>
              </Box>
              <Card variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)' }}>
                <CardContent sx={{ p: 0 }}>
                  <List dense>
                    {shelfStatus.pending.length === 0 ? (
                      <ListItem divider>
                        <ListItemText primary="No pending shelves" secondary="All warehouse shelves have been counted!" />
                      </ListItem>
                    ) : (
                      shelfStatus.pending
                        .slice(pendingShelfPage * DEFAULT_PAGE_SIZE, pendingShelfPage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                        .map((shelf) => (
                          <ListItem key={shelf.shelfCode} divider sx={{ py: 1.5 }}>
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="subtitle1" fontWeight={700}>{shelf.shelfCode}</Typography>
                                  <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                    {shelf.quantity.toLocaleString()} units
                                  </Typography>
                                </Box>
                              }
                              secondary="Never included in any cyclic count"
                            />
                            <Chip label="PENDING" size="small" variant="outlined" color="warning" sx={{ ml: 2, fontWeight: 700 }} />
                          </ListItem>
                        ))
                    )}
                  </List>
                  <TablePaginationBar
                    page={pendingShelfPage}
                    total={shelfStatus.pending.length}
                    onPageChange={setPendingShelfPage}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Counted Shelves */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" mr={1.5}>Counted Shelves</Typography>
                  <Chip label={shelfStatus.counted.length} size="small" color="success" sx={{ fontWeight: 700 }} />
                </Box>
              </Box>
              <Card variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)' }}>
                <CardContent sx={{ p: 0 }}>
                  <List dense>
                    {shelfStatus.counted.length === 0 ? (
                      <ListItem divider>
                        <ListItemText primary="No shelves counted yet" secondary="Start a cyclic count to track progress" />
                      </ListItem>
                    ) : (
                      shelfStatus.counted
                        .slice(countedShelfPage * DEFAULT_PAGE_SIZE, countedShelfPage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                        .map((shelf) => (
                          <ListItem key={shelf.shelfCode} divider sx={{ py: 1.5 }}>
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="subtitle1" fontWeight={700}>{shelf.shelfCode}</Typography>
                                  <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                    {shelf.quantity.toLocaleString()} units
                                  </Typography>
                                </Box>
                              }
                              secondary="Successfully verified in cyclic counts"
                            />
                            <Chip label="COUNTED" size="small" color="success" sx={{ ml: 2, fontWeight: 700 }} />
                          </ListItem>
                        ))
                    )}
                  </List>
                  <TablePaginationBar
                    page={countedShelfPage}
                    total={shelfStatus.counted.length}
                    onPageChange={setCountedShelfPage}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )
      )}
    </Box>
  );

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Card sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" mb={1}>
          New Cyclic Count
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Enter a unique reference number to identify this cyclic count session.
        </Typography>
        <TextField
          label="Cyclic Count Reference Number"
          fullWidth
          value={refNumber}
          onChange={(e) => setRefNumber(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreateCount()}
          placeholder="e.g. CC-2026-001"
          helperText="This will be used to track and audit this count"
          error={!!setupError}
          sx={{ mb: 2 }}
        />
        {setupError && <Alert severity="error" sx={{ mb: 2 }}>{setupError}</Alert>}
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" size="large" fullWidth onClick={handleBackToList} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleCreateCount}
            disabled={creatingCount || !refNumber.trim()}
            startIcon={creatingCount ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
          >
            {creatingCount ? 'Creating…' : 'Start Cyclic Count'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderStep1 = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">Add Shelves</Typography>
            <Chip label={cyclicCount?.refNumber} size="small" color="primary" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Enter each shelf code you want to count. The system will snapshot the current inventory for comparison.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Shelf Code"
              value={shelfInput}
              onChange={(e) => setShelfInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddShelf()}
              placeholder="e.g. SHELF-A1"
              size="small"
              sx={{ flex: 1 }}
              error={!!shelfError}
            />
            <Button
              variant="contained"
              onClick={handleAddShelf}
              disabled={addingShelf || !shelfInput.trim()}
              startIcon={addingShelf ? <CircularProgress size={16} color="inherit" /> : <Add />}
              sx={{ minWidth: 140, borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
            >
              {addingShelf ? 'Adding…' : 'Add Shelf'}
            </Button>
          </Box>
          {shelfError && <Alert severity="error" sx={{ mb: 2 }}>{shelfError}</Alert>}
          {shelfWarning && <Alert severity="warning" sx={{ mb: 2 }}>{shelfWarning}</Alert>}
          {shelfSuccess && <Alert severity="success" sx={{ mb: 2 }}>{shelfSuccess}</Alert>}

          {cyclicCount && cyclicCount.shelves.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                Selected Shelves ({cyclicCount.shelves.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {cyclicCount.shelves.map((shelf) => {
                  const count = cyclicCount.baselineInventory.filter((b) => b.shelfCode === shelf).length;
                  return (
                    <Chip
                      key={shelf}
                      label={`${shelf} · ${count} SKUs`}
                      onDelete={() => handleRemoveShelf(shelf)}
                      deleteIcon={<Delete />}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {cyclicCount && cyclicCount.shelves.length > 0 && (
        <Box sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleProceedToCount}
            disabled={proceedingToCount}
            startIcon={proceedingToCount ? <CircularProgress size={20} color="inherit" /> : <FactCheck />}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
          >
            {proceedingToCount ? 'Refreshing Stock…' : 'Proceed to Count'}
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderStep2 = () => {
    if (!cyclicCount) return null;
    const shelves = cyclicCount.shelves;

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Enter the actual quantity you physically counted for each SKU on each shelf. You can also add new SKUs that weren&apos;t in the baseline.
        </Alert>

        {shelves.map((shelf) => {
          const baselineItems = cyclicCount.baselineInventory.filter((b) => b.shelfCode === shelf);
          // Include any new SKUs the user added via newSkuInputs that are already in countInputs
          const extraKeys = Object.keys(countInputs).filter((k) => {
            const [s, sku] = k.split('::');
            return s === shelf && !baselineItems.find((b) => String(b.skuCode || '').toUpperCase() === String(sku || '').toUpperCase());
          });

          return (
            <Card key={shelf} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6">{shelf}</Typography>
                  <Chip label={`${baselineItems.length} baseline SKUs`} size="small" color="info" variant="outlined" />
                </Box>
                {/* Horizontal-scroll wrap (CardingTab pattern): guards the 3-column count table
                    (with its fixed-width number input) from overflowing on narrow widths. */}
                <TableContainer sx={{ borderRadius: '14px', border: '1px solid #ECECE4', overflow: 'hidden', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 360 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#faf8f3', '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                        <TableCell>SKU Code</TableCell>
                        <TableCell align="center">Baseline Qty</TableCell>
                        <TableCell align="center">Counted Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {baselineItems.map((item) => (
                        <TableRow key={item.skuCode} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.skuCode}</TableCell>
                          <TableCell align="center">
                            <Chip label={item.baselineQty} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={countInputs[`${shelf}::${item.skuCode}`] ?? item.baselineQty}
                              onChange={(e) => handleCountChange(shelf, item.skuCode, e.target.value)}
                              inputProps={{ min: 0, style: { textAlign: 'center', width: 90 } }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Extra SKUs added by user */}
                      {extraKeys.map((key) => {
                        const sku = key.split('::')[1];
                        return (
                          <TableRow key={key} hover sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12) }}>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'warning.dark' }}>
                              {sku} <Chip label="New" size="small" color="warning" sx={{ ml: 1 }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={0} size="small" variant="outlined" color="warning" />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={countInputs[key] ?? 0}
                                onChange={(e) => handleCountChange(shelf, sku, e.target.value)}
                                inputProps={{ min: 0, style: { textAlign: 'center', width: 90 } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Add new SKU to this shelf */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Add SKU not in baseline
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={skuOptions}
                      getOptionLabel={(option: any) => option.sku || ''}
                      loading={searchingSku}
                      onInputChange={(event, newInputValue) => {
                        handleSkuSearch(newInputValue);
                        handleNewSkuChange(shelf, 'sku', newInputValue);
                      }}
                      onChange={(event, newValue: any) => {
                        handleNewSkuChange(shelf, 'sku', newValue?.sku || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="SKU Code"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {searchingSku ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Qty"
                      type="number"
                      size="small"
                      value={newSkuInputs[shelf]?.qty ?? 0}
                      onChange={(e) => handleNewSkuChange(shelf, 'qty', e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: 100 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleAddNewSku(shelf)}
                      disabled={!newSkuInputs[shelf]?.sku?.trim()}
                      startIcon={<Add />}
                      sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}

        {countError && <Alert severity="error" sx={{ mb: 2 }}>{countError}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            sx={{ mr: 2, borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
            onClick={() => setActiveStep(1)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmitCount}
            disabled={submittingCount || !canEdit}
            startIcon={submittingCount ? <CircularProgress size={20} color="inherit" /> : <Done />}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
          >
            {submittingCount ? 'Calculating Variance…' : 'Submit Count'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStep3 = () => {
    if (!cyclicCount) return null;
    const adjustments = cyclicCount.adjustments;
    const withVariance = adjustments.filter((a) => a.operation !== 'NONE');
    const noVariance = adjustments.filter((a) => a.operation === 'NONE');
    const pendingAdj = withVariance.filter((a) => !a.adjusted);
    const doneAdj = withVariance.filter((a) => a.adjusted);

    return (
      <Box>
        {/* Summary cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
          <Card sx={{ border: '1px solid', borderColor: 'success.light', bgcolor: (t) => alpha(t.palette.success.main, 0.12) }}>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{noVariance.length}</Typography>
              <Typography variant="caption" color="text.secondary">No Variance</Typography>
            </CardContent>
          </Card>
          <Card sx={{ border: '1px solid', borderColor: 'warning.light', bgcolor: (t) => alpha(t.palette.warning.main, 0.12) }}>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{pendingAdj.length}</Typography>
              <Typography variant="caption" color="text.secondary">Pending Adjustments</Typography>
            </CardContent>
          </Card>
          <Card sx={{ border: '1px solid', borderColor: 'info.light', bgcolor: (t) => alpha(t.palette.info.main, 0.12) }}>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{doneAdj.length}</Typography>
              <Typography variant="caption" color="text.secondary">Adjusted</Typography>
            </CardContent>
          </Card>
        </Box>

        {adjustError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAdjustError('')}>{adjustError}</Alert>}

        {/* Variance table */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Variance Report</Typography>
              {pendingAdj.length > 0 && canEdit && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setConfirmAdjustAll(true)}
                  disabled={adjustAllLoading}
                  startIcon={adjustAllLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
                >
                  Adjust All ({pendingAdj.length})
                </Button>
              )}
            </Box>
            {adjustAllLoading && <LinearProgress sx={{ mb: 2 }} />}
            {isMobile ? (
              /* Mobile card view (<md). Same sorted+paginated adjustments, same handlers,
                 same shared sort state surfaced via VarianceSortControl. No horizontal overflow. */
              <Box>
                <VarianceSortControl
                  sortKey={varianceSortKey}
                  sortDir={varianceSortDir}
                  onSortKeyChange={handleVarianceSort}
                  onToggleDir={toggleVarianceDir}
                />
                {adjustments.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No items to review</Box>
                ) : (
                  <Stack spacing={1.5}>
                    {sortAdjustments(adjustments, varianceSortKey, varianceSortDir)
                      .slice(variancePage * DEFAULT_PAGE_SIZE, variancePage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                      .map((adj) => (
                      <Card
                        key={adj._id}
                        variant="outlined"
                        sx={{
                          borderRadius: '14px',
                          bgcolor: adj.adjusted
                            ? (t) => alpha(t.palette.success.main, 0.12)
                            : adj.operation === 'NONE'
                            ? 'transparent'
                            : adj.operation === 'ADD'
                            ? (t) => alpha(t.palette.warning.main, 0.12)
                            : (t) => alpha(t.palette.error.main, 0.12),
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <Box>
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{adj.skuCode}</Typography>
                              <Chip label={adj.shelfCode} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                            </Box>
                            <VarianceBadge variance={adj.variance} />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                            <Box>
                              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Baseline</Typography>
                              <Typography variant="body2" fontWeight={600}>{adj.baselineQty}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Counted</Typography>
                              <Typography variant="body2" fontWeight={600}>{adj.countedQty}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Action</Typography>
                              {adj.operation === 'NONE' ? (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              ) : (
                                <Chip
                                  label={adj.operation === 'ADD' ? `+${adj.variance} in UC` : `${adj.variance} in UC`}
                                  size="small"
                                  color={adj.operation === 'ADD' ? 'warning' : 'error'}
                                  variant="outlined"
                                  sx={{ mt: 0.25 }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {adj.adjusted ? (
                              <Chip icon={<CheckCircle fontSize="small" />} label="Adjusted" size="small" color="success" variant="outlined" />
                            ) : adj.operation === 'NONE' ? (
                              <Chip icon={<CheckCircle fontSize="small" />} label="No action needed" size="small" variant="outlined" />
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color={adj.operation === 'ADD' ? 'warning' : 'error'}
                                onClick={() => handleAdjustOne(adj)}
                                disabled={!!adjusting[adj._id] || !canEdit}
                                startIcon={
                                  adjusting[adj._id] ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : adj.operation === 'ADD' ? (
                                    <TrendingUp fontSize="small" />
                                  ) : (
                                    <TrendingDown fontSize="small" />
                                  )
                                }
                                sx={{ minWidth: 90, borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                              >
                                {adjusting[adj._id] ? '…' : 'Adjust'}
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#faf8f3', '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                    <TableCell sortDirection={varianceSortKey === 'sku' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'sku'} direction={varianceSortKey === 'sku' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('sku')}>
                        SKU Code
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={varianceSortKey === 'shelf' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'shelf'} direction={varianceSortKey === 'shelf' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('shelf')}>
                        Shelf
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'expected' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'expected'} direction={varianceSortKey === 'expected' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('expected')}>
                        Baseline
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'counted' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'counted'} direction={varianceSortKey === 'counted' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('counted')}>
                        Counted
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'variance' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'variance'} direction={varianceSortKey === 'variance' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('variance')}>
                        Variance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Action</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortAdjustments(adjustments, varianceSortKey, varianceSortDir)
                    .slice(variancePage * DEFAULT_PAGE_SIZE, variancePage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                    .map((adj) => (
                    <TableRow
                      key={adj._id}
                      hover
                      sx={{
                        bgcolor: adj.adjusted
                          ? (t) => alpha(t.palette.success.main, 0.12)
                          : adj.operation === 'NONE'
                          ? 'transparent'
                          : adj.operation === 'ADD'
                          ? (t) => alpha(t.palette.warning.main, 0.12)
                          : (t) => alpha(t.palette.error.main, 0.12),
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{adj.skuCode}</TableCell>
                      <TableCell>
                        <Chip label={adj.shelfCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">{adj.baselineQty}</TableCell>
                      <TableCell align="center">{adj.countedQty}</TableCell>
                      <TableCell align="center">
                        <VarianceBadge variance={adj.variance} />
                      </TableCell>
                      <TableCell align="center">
                        {adj.operation === 'NONE' ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          <Chip
                            label={adj.operation === 'ADD' ? `+${adj.variance} in UC` : `${adj.variance} in UC`}
                            size="small"
                            color={adj.operation === 'ADD' ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {adj.adjusted ? (
                          <Tooltip title="Adjusted in Unicommerce">
                            <CheckCircle color="success" fontSize="small" />
                          </Tooltip>
                        ) : adj.operation === 'NONE' ? (
                          <Tooltip title="No action needed">
                            <CheckCircle color="disabled" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color={adj.operation === 'ADD' ? 'warning' : 'error'}
                            onClick={() => handleAdjustOne(adj)}
                            disabled={!!adjusting[adj._id] || !canEdit}
                            startIcon={
                              adjusting[adj._id] ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : adj.operation === 'ADD' ? (
                                <TrendingUp fontSize="small" />
                              ) : (
                                <TrendingDown fontSize="small" />
                              )
                            }
                            sx={{ minWidth: 90, borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                          >
                            {adjusting[adj._id] ? '…' : 'Adjust'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {adjustments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No items to review
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePaginationBar
              page={variancePage}
              total={adjustments.length}
              onPageChange={setVariancePage}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button variant="outlined" onClick={() => setActiveStep(2)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}>Back to Count</Button>
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleMarkComplete}
            disabled={pendingAdj.length > 0 || !canEdit}
            startIcon={<Done />}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
          >
            {pendingAdj.length > 0 ? `Complete after ${pendingAdj.length} adjustment${pendingAdj.length !== 1 ? 's' : ''}` : 'Mark as Complete'}
          </Button>
        </Box>

        {/* Confirm adjust all dialog */}
        <Dialog open={confirmAdjustAll} onClose={() => setConfirmAdjustAll(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Bulk Adjustment</DialogTitle>
          <DialogContent>
            <Typography>
              This will push <strong>{pendingAdj.length}</strong> variance adjustment{pendingAdj.length !== 1 ? 's' : ''} to Unicommerce. Each ADD variance will increase inventory and each SUBTRACT variance will decrease it.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAdjustAll(false)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
            <Button variant="contained" color="warning" onClick={handleAdjustAll} disabled={adjustAllLoading || !canEdit} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}>
              {adjustAllLoading ? 'Adjusting…' : 'Confirm & Adjust All'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderStep4 = () => {
    if (!cyclicCount) return null;
    const total = cyclicCount.adjustments.length;
    const adjusted = cyclicCount.adjustments.filter((a) => a.adjusted).length;
    const noVariance = cyclicCount.adjustments.filter((a) => a.operation === 'NONE').length;
    const failed = total - adjusted - noVariance;

    return (
      <Box>
        <Card sx={{ maxWidth: 760, mx: 'auto', mt: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" mb={1}>
              Cyclic Count Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Reference: <strong>{cyclicCount.refNumber}</strong>
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}>
                <Typography variant="h4" color="primary.main">{cyclicCount.shelves.length}</Typography>
                <Typography variant="caption" color="text.secondary">Shelves Counted</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.12) }}>
                <Typography variant="h4" color="success.main">{adjusted}</Typography>
                <Typography variant="caption" color="text.secondary">Adjustments Made</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: failed > 0 ? (t) => alpha(t.palette.error.main, 0.12) : 'grey.100' }}>
                <Typography variant="h4" color={failed > 0 ? 'error.main' : 'text.secondary'}>{failed}</Typography>
                <Typography variant="caption" color="text.secondary">Failed</Typography>
              </Box>
            </Box>
            {failed > 0 && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                {failed} adjustment{failed !== 1 ? 's' : ''} could not be pushed to Unicommerce. Please review and retry manually.
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleBackToList}
              startIcon={<ArrowBack />}
              sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
            >
              Back to Cyclic Count List
            </Button>
          </CardContent>
        </Card>

        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" mb={0.5}>Variance Report</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Final snapshot of baseline vs physical count and adjustment status.
            </Typography>
            {isMobile ? (
              /* Mobile card view (<md) — read-only final snapshot. Same sorted+paginated data,
                 same shared sort state surfaced via VarianceSortControl. No horizontal overflow. */
              <Box>
                <VarianceSortControl
                  sortKey={varianceSortKey}
                  sortDir={varianceSortDir}
                  onSortKeyChange={handleVarianceSort}
                  onToggleDir={toggleVarianceDir}
                />
                <Stack spacing={1.5}>
                  {sortAdjustments(cyclicCount.adjustments, varianceSortKey, varianceSortDir)
                    .slice(variancePage * DEFAULT_PAGE_SIZE, variancePage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                    .map((adj) => (
                    <Card key={adj._id} variant="outlined" sx={{ borderRadius: '14px' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <Box>
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{adj.skuCode}</Typography>
                            <Chip label={adj.shelfCode} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                          </Box>
                          <VarianceBadge variance={adj.variance} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <Box>
                              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Baseline</Typography>
                              <Typography variant="body2" fontWeight={600}>{adj.baselineQty}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Counted</Typography>
                              <Typography variant="body2" fontWeight={600}>{adj.countedQty}</Typography>
                            </Box>
                          </Box>
                          {adj.adjusted || adj.operation === 'NONE' ? (
                            <CheckCircle color="success" fontSize="small" />
                          ) : (
                            <Chip label="Pending" size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', border: '1px solid #ECECE4', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#faf8f3', '& .MuiTableCell-root': { ...eyebrow, color: '#8A8078', fontSize: '0.6rem' } }}>
                    <TableCell sortDirection={varianceSortKey === 'sku' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'sku'} direction={varianceSortKey === 'sku' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('sku')}>
                        SKU Code
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={varianceSortKey === 'shelf' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'shelf'} direction={varianceSortKey === 'shelf' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('shelf')}>
                        Shelf
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'expected' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'expected'} direction={varianceSortKey === 'expected' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('expected')}>
                        Baseline
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'counted' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'counted'} direction={varianceSortKey === 'counted' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('counted')}>
                        Counted
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sortDirection={varianceSortKey === 'variance' ? varianceSortDir : false}>
                      <TableSortLabel active={varianceSortKey === 'variance'} direction={varianceSortKey === 'variance' ? varianceSortDir : 'asc'} onClick={() => handleVarianceSort('variance')}>
                        Variance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortAdjustments(cyclicCount.adjustments, varianceSortKey, varianceSortDir)
                    .slice(variancePage * DEFAULT_PAGE_SIZE, variancePage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE)
                    .map((adj, index) => (
                    <TableRow key={adj._id} hover sx={{ bgcolor: index % 2 === 0 ? 'transparent' : 'grey.50' }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{adj.skuCode}</TableCell>
                      <TableCell>
                        <Chip label={adj.shelfCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">{adj.baselineQty}</TableCell>
                      <TableCell align="center">{adj.countedQty}</TableCell>
                      <TableCell align="center">
                        <VarianceBadge variance={adj.variance} />
                      </TableCell>
                      <TableCell align="center">
                        {adj.adjusted || adj.operation === 'NONE' ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Chip label="Pending" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePaginationBar
              page={variancePage}
              total={cyclicCount.adjustments.length}
              onPageChange={setVariancePage}
            />
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderWizardView = () => (
    <Box>
      {/* Back affordance kept above the PageHeader so it stays visible on xs (PageHeader actions
          are hidden on mobile). */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          All Cyclic Counts
        </Button>
      </Box>
      <PageHeader
        icon={<FactCheck sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
        title="Cyclic Count"
        subtitle="Physical inventory count, variance detection, and Unicommerce adjustment"
      />

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {STEPS.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && renderStep0()}
      {activeStep === 1 && renderStep1()}
      {activeStep === 2 && renderStep2()}
      {activeStep === 3 && renderStep3()}
      {activeStep === 4 && renderStep4()}
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2, pb: 3 }}>
      {pageView === 'list' ? renderListView() : renderWizardView()}

    </Box>
  );
};

export default CyclicCountPage;
